/**
 * Shared types for PDF worker communication.
 *
 * All data passed between the main thread and workers must be
 * structured-cloneable (plain JSON + typed arrays). No functions,
 * DOM nodes, or class instances.
 */

import type { Template, PdfLayoutPlan } from '@wysiwyg/pdf-export';
import type { FontSource } from '@wysiwyg/pdf-export';
import type { ThemeVariableType } from '../wysiwyg/types';
import type { RawDataContext } from '../wysiwyg/textFormatter';
import type { ThemeVariable } from '../wysiwyg/types';

export interface SerializableResolverContext {
  data: Record<string, any>;
  rawDataContext?: RawDataContext;
  variableExamples?: Record<string, string>;
  conditionTypeByKey?: Record<string, ThemeVariableType>;
  themeVariables?: ThemeVariable[];
  legacyImages?: Record<string, string>;
  legacyFonts?: Record<string, string>;
  newAssetImages?: Record<string, string>;
  newAssetFonts?: Record<string, string>;
  defaultFont?: string;
  isPreview?: boolean;
  hasCoa?: boolean;
}


export interface PdfWorkerJob {
  /** Which reel this job is for (1-based) */
  reelNum: number;

  /** Template definition (plain JSON) */
  template: Template;

  /** Pre-computed layout plan */
  layout: PdfLayoutPlan;

  /** Number of labels for this reel */
  labelCount: number;

  /** QR offset into the global qrValues array */
  qrIndexOffset: number;

  /** QR values relevant to this reel */
  qrValues: string[];

  /** Common data merged with theme data */
  commonData: Record<string, any>;

  /** Theme data for variable overrides */
  themeData: Record<string, any>;

  /** Serializable resolver context for reconstructing resolver functions */
  resolverContext: SerializableResolverContext;

  /** Font sources for PDF embedding */
  fontSources: FontSource[];

  /** Base URL for resolving relative font/image URLs (replaces window.location.origin) */
  baseUrl: string;

  /** Default font family */
  defaultFont: string;

  /** Whether this is a preview PDF */
  isPreview: boolean;
}

export interface PdfWorkerRequest {
  type: 'generate';
  job: PdfWorkerJob;
}

export interface PdfWorkerSuccess {
  type: 'success';
  reelNum: number;
  pdfBytes: Uint8Array;
}

export interface PdfWorkerError {
  type: 'error';
  reelNum: number;
  error: string;
}

export type PdfWorkerResponse = PdfWorkerSuccess | PdfWorkerError;

export interface ReelAssignment {
  reelNum: number;
  labelCount: number;
  qrIndexOffset: number;
  rangeStart: number;
  rangeEnd: number;
  idbKey: string;
  filename: string;
}

export interface ReelResult {
  reelNum: number;
  pdfBytes: Uint8Array;
}

export const DEFAULT_WORKER_COUNT = 2;
export const MAX_WORKER_COUNT = 8;
